import { jsx as _jsx } from "react/jsx-runtime";
import { createContext, useContext, useLayoutEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { useDialogWege } from "./dialogWege.js";
/**
 * Was die zwei strikten Hüllen über `me-dialog` gemeinsam haben — `Modal` (die Masken) und
 * `Arbeitsdialog` (der vollflächige Editor, Paket „Editor-Arbeitsdialog“, Aufgabe 1).
 *
 * Bis zum 09.10.2026 stand all das allein in `Modal.tsx`. Eine zweite Hülle hätte es
 * abschreiben müssen — und eine Abschrift, die beim nächsten Befund nur an einer Stelle
 * nachgezogen wird, ist genau der Fehler, gegen den die Hausregel „striktes Modalpanel“
 * als EIN Baustein geschrieben wurde. Hier steht deshalb:
 *
 *  - die Wege hinaus (`useStrikteWege`): jedes `me-abbrechen` abgefangen, der Riegel
 *    `abbrechenGesperrt` am Rahmen, Öffnen im selben Commit;
 *  - der Kontext `ImModal` und das Portal für Subdialoge (`StrikteEbene`, Review K1/K2).
 *
 * Was je Hülle verschieden ist — Breite, Lage, Art, Slots, `unter` — bleibt in der Hülle.
 *
 * Seit me-design 3311f91 (10.10.2026) kann `me-dialog` drei Dinge selbst, die hier bis dahin
 * nachgebaut standen: Ein seitlicher Subdialog ohne Platz neben seinem Eltern fällt auf
 * „stapel" zurück (früher `passtNebeneinander` im `Modal` samt Breitenmeldung über
 * `ImModal`); ein natives Schliessen am Riegel vorbei (nicht abbrechbares `cancel`,
 * Android-Zurück, CloseWatcher) läuft über `me-abbrechen` und öffnet bei Widerspruch sofort
 * wieder (früher W1: Wiederöffnen über `me-geschlossen`); der innere `<dialog>` trägt den
 * Titeltext als `aria-label` (früher `aria-label={titel}` am Host). Seit aff0190 gibt das
 * Element beim Aushängen den Fokus selbst an den Auslöser zurück — auch in Safari über den
 * letzten Zeiger-Klick (Befunde 13 und 35); die eigene Rückgabe samt Klick-Horcher, die hier
 * bis dahin stand (Aufgabe 8), entfällt. Seit 8cc13b0 (P4-1) wiederholt das Element die
 * Rückgabe auch an einen Auslöser, der beim Aushängen noch gesperrt ist (React räumt den
 * Dialog vor dem Freischalten des Knopfs ab) — damit fiel in ME-Etappe 4 (P8) auch die letzte
 * Rückgabe von Hand in web („Vorlage anwenden“ im Angebot). Die Hülle hält deshalb nichts
 * mehr am Fokus fest: Rückgabe beim Schliessen und Einstiegsfokus (`anfangsfokus`, sonst das
 * erste Feld) sind Sache des Elements. Die zwei Übergänge aus P8 (autofocus als Attribut
 * über eine Ref, Zeiger-Horcher beim Laden) fielen mit me-design 48e39f9 weg: `Knopf autoFocus`
 * setzt das Attribut `anfangsfokus`, der Zeiger-Horcher läuft ab Modulladen.
 */
/**
 * Steht diese Hülle im Inhalt einer anderen? Dann ist sie ein Subdialog und wird per Portal
 * an `document.body` gezeichnet (Review Aufgabe 1, K1/K2): Die Eltern-Maske bekommt von
 * me-dialog `data-neben`/`data-hinten` (`pointer-events: none`) bzw. am Telefon
 * `data-versteckt` (`display: none`), und beides erbte sich über den Flat Tree in einen
 * Subdialog in ihrem Inhalt — nicht zu treffen, am Telefon unsichtbar bei gesperrter Seite.
 * Masken der obersten Ebene bleiben, wo sie stehen (CSS-Kontext ihres Bereichs).
 *
 * Eine Breite meldet der Kontext nicht mehr: Ob ein seitlicher Subdialog neben seinen Eltern
 * passt, rechnet `me-dialog` seit 3311f91 selbst aus den `breite`-Angaben des Stapels —
 * neben `breite="voll"` (Arbeitsdialog) passt nie etwas, jeder Subdialog wird gestapelt.
 */
export const ImModal = createContext(false);
/** true, wenn diese Hülle im Inhalt einer anderen steht (also ein Subdialog ist). */
export function useImModal() {
    return useContext(ImModal);
}
/**
 * Die Wege hinaus einer strikten Hülle. Liefert die zwei Refs, die die Hülle an ihren
 * `Dialog` hängt: `anker` in den Titel (darüber findet `useDialogWege` den Host) und
 * `steuerung` als Ref des Dialogs.
 *
 * Die Arbeitsteilung am Riegel, damit sie nicht geraten werden muss:
 *
 *  - **Der RAHMEN hält die Taste** (und jedes native Schliessen): Das Element meldet beides
 *    als `me-abbrechen`, und hier wird JEDES `me-abbrechen` abgefangen (`preventDefault`) —
 *    das Element schliesst nie von selbst; hat der Browser die Ebene schon geschlossen
 *    (Escape mit Body-Fokus, Android-Zurück), öffnet das Element sie wegen des Widerspruchs
 *    sofort wieder. Geschlossen wird, indem der Aufrufer die Hülle aushängt; so bleiben
 *    React-Zustand und Dialog nie uneins. Ist der Ausgang gesperrt, geht der Rückruf gar
 *    nicht erst los (Fixrunde 1, 05.09.2026: ein Riegel allein am Knopf liesse die Tastatur
 *    daran vorbei). Auch das Kreuz im Kopf (seit Entscheid 36 in jedem Dialog) meldet `me-abbrechen` und geht damit durch denselben Riegel.
 *  - **Die FUSSLEISTE hält den Knopf** (`ModalFuss` bzw. die Knöpfe des Aufrufers im Fuss
 *    des Arbeitsdialogs): Ein gesperrter Knopf löst gar nicht erst aus.
 *  - **Der Aufrufer hält seinen eigenen Weg.** Die `abbrechen()`-Funktionen prüfen den
 *    Arbeitszustand weiterhin selbst.
 */
export function useStrikteWege(p) {
    const anker = useRef(null);
    const steuerung = useRef(null);
    useDialogWege(anker, {
        beiAbbrechen: (ereignis) => {
            ereignis.preventDefault();
            if (p.abbrechenGesperrt === true)
                return;
            void p.onAbbrechen();
        },
    });
    /*
     * Im selben Commit öffnen, in dem die Hülle eingehängt wird (Layout-Effekt): Das
     * kontrollierte `offen` öffnet erst im späteren Effekt — bis dahin stünde ein
     * eingehängter, aber geschlossener Dialog im Dokument, dessen Felder schon da sind und
     * dessen Rahmen fehlt. `offen` bleibt trotzdem gesetzt; es findet den Dialog dann offen.
     * Nach `useDialogWege`: Die Riegel hängen, bevor der Dialog aufgeht.
     */
    useLayoutEffect(() => { steuerung.current?.oeffnen(); }, []);
    return { anker, steuerung };
}
/**
 * Die Ebene einer strikten Hülle: sagt Subdialogen in ihrem Inhalt, dass sie in einer Hülle
 * stehen (`ImModal`), und zeichnet sich selbst per Portal an `document.body`, wenn sie
 * ihrerseits in einer Hülle steht.
 */
export function StrikteEbene(p) {
    const ebene = _jsx(ImModal.Provider, { value: true, children: p.children });
    return p.imModal ? createPortal(ebene, document.body) : ebene;
}
//# sourceMappingURL=strikteHuelle.js.map